import { useMutation, useQuery } from "@tanstack/react-query";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { get, post } from "@/api";
import { LocalStorageService } from "@/modules/storage/StorageService/StorageService";
import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";

import { LocationListPickerV3 } from "../components/LocationListPickerV3/LocationListPickerV3";
import OnboardingHeaderV3 from "../components/OnboardingScreenHeader/OnboardingHeaderV3";
import {
  PracticeLocation,
  transformLocationsByCity,
} from "../utils/locationTransform";

interface LocationPickerScreenProps {
  locations?: PracticeLocation[];
  onLocationSelect?: (locationId: string) => void | Promise<void>;
  selectedLocation?: string;
  className?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  isLoading?: boolean;
  loadingMessage?: string;
  hideFindNearby?: boolean;
  onFindPracticeClick?: () => void;
}

export function LocationPickerScreen({
  locations = [],
  onLocationSelect,
  selectedLocation = "",
  className,
  prefix,
  suffix,
  isLoading = false,
  loadingMessage = "Loading locations...",
  hideFindNearby = false,
  onFindPracticeClick,
}: Readonly<LocationPickerScreenProps>) {
  const navigate = useNavigate();
  const [currentSelection, setCurrentSelection] = useState<string[]>(
    selectedLocation ? [selectedLocation] : []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  // Transform locations into city groups for V3 component
  const cityGroups = useMemo(
    () => transformLocationsByCity(locations),
    [locations]
  );

  const handleSelectionChange = (selectedIds: string[]) => {
    setCurrentSelection(selectedIds);
    // Pass the selectedIds directly to avoid race condition with state updates
    if (selectedIds.length > 0) {
      handleContinue(selectedIds[0]);
    }
  };

  const handleContinue = async (locationIdOverride?: string) => {
    // Use override if provided (from selection), otherwise use state (from button click)
    const locationId = locationIdOverride ?? currentSelection[0];
    if (!locationId || !onLocationSelect) return;

    setIsSubmitting(true);
    setSubmitError(undefined);

    try {
      await onLocationSelect(locationId);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "An error occurred. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const { data: user } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
  });

  const hasSelection = currentSelection.length > 0;
  const isButtonDisabled =
    isLoading || isSubmitting || (!hasSelection && !isLoading);

  const { mutate: recordReferralSignupStarted } = useMutation({
    mutationFn: (statelessCode: string) =>
      post("/incentives/record-referral-signup-started", {
        statelessCode: statelessCode,
        personId: user?.id as string,
        platformId: user?.platform?.id as string,
      }),
    onSuccess: () => {
      console.log("Referral signup started");
    },
  });

  useEffect(() => {
    const statelessCode = LocalStorageService.getItem("statelessReferralCode");

    if (statelessCode && user?.id && user?.platform?.id) {
      recordReferralSignupStarted(statelessCode);
    }
  }, [user]);

  return (
    <div className={`flex w-full flex-col gap-[36px] ${className}`}>
      {/* Optional prefix slot for logo */}
      {prefix ? (
        <div className="flex w-full items-center justify-center">{prefix}</div>
      ) : null}

      <div className="flex flex-col gap-[24px]">
        {/* Header */}
        <OnboardingHeaderV3
          title="Which is your preferred location?"
          align="left"
        />

        {/* Loading state */}
        {isLoading && (
          <div className="text-center">
            <div className="text-sm text-secondary">{loadingMessage}</div>
          </div>
        )}

        {/* Location options */}
        {!isLoading && locations && locations.length > 0 && (
          <LocationListPickerV3
            items={cityGroups}
            multiSelect={false}
            onSelectionChange={handleSelectionChange}
            initialSelectedIds={selectedLocation ? [selectedLocation] : []}
          />
        )}

        {/* No locations available */}
        {!isLoading && (!locations || locations.length === 0) && (
          <div className="text-center">
            <div className="text-sm text-secondary">
              No locations available at this time.
            </div>
          </div>
        )}
      </div>

      {/* Submit Error */}
      {submitError && (
        <div className="text-center text-sm text-red">{submitError}</div>
      )}

      {/* Continue Button */}
      <Button
        fullWidth
        disabled={isButtonDisabled}
        onClick={() => handleContinue()}
      >
        {isSubmitting ? "Loading..." : "Continue"}
      </Button>

      {/* Find a practice near you - link to platform picker */}
      {!hideFindNearby && (
        <div className="text-center">
          <TextButton
            onClick={
              onFindPracticeClick || (() => navigate("/signin/platform-picker"))
            }
            xsText
            className="mx-auto"
          >
            Find a practice near you
          </TextButton>
        </div>
      )}

      {/* Optional suffix slot */}
      {suffix ? <div className="w-full">{suffix}</div> : null}
    </div>
  );
}
